import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Formik, Field } from 'formik';
import { formatarDocumento, limparDocumento } from 'components/Utils/documentoAlfanumerico';
import useDrawerFiltrosHook from '../../hooks/useDrawerFiltrosHook';
import './DrawerFiltros.css';

const manterSomenteNumeros = valor => String(valor || '').replace(/\D/g, '');

const aplicarMascaraCNPJ = valor => formatarDocumento(limparDocumento(valor).slice(0, 14));

const aplicarMascaraValor = valor => {
  const valorNumerico = manterSomenteNumeros(valor).slice(0, 15);
  if (!valorNumerico) return '';

  const valorEmCentavos = Number(valorNumerico);
  const valorFormatado = (valorEmCentavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `R$ ${valorFormatado}`;
};

const aplicarMascaraCampo = (valor, mascara) => {
  if (mascara === 'cnpj') return aplicarMascaraCNPJ(valor);
  if (mascara === 'numero') return manterSomenteNumeros(valor);
  if (mascara === 'valor') return aplicarMascaraValor(valor);
  return valor;
};

const CampoTexto = ({
  label,
  name,
  type = 'text',
  placeholder = '',
  mascara = null,
  inputProps = {},
  ...props
}) => (
  <Box className="drawer-campo">
    <Typography variant="caption" className="drawer-campo-label">
      {label}
    </Typography>
    <Field name={name}>
      {({ field }) => (
        <TextField
          {...field}
          size="small"
          fullWidth
          type={mascara ? 'text' : type}
          placeholder={placeholder}
          variant="outlined"
          InputLabelProps={type === 'date' ? { shrink: true } : undefined}
          value={field.value || ''}
          onChange={event => {
            const valorComMascara = aplicarMascaraCampo(event.target.value, mascara);
            field.onChange({
              target: {
                name: field.name,
                value: valorComMascara
              }
            });
          }}
          inputProps={{
            ...inputProps,
            ...(mascara === 'cnpj' ? { inputMode: 'text' } : mascara ? { inputMode: 'numeric' } : {})
          }}
          {...props}
        />
      )}
    </Field>
  </Box>
);

const CampoSelect = ({ label, name, opcoes, placeholder = 'Selecione...', carregando = false }) => (
  <Box className="drawer-campo">
    <Typography variant="caption" className="drawer-campo-label">
      {label}
    </Typography>
    <Field name={name}>
      {({ field }) => (
        <Select
          {...field}
          size="small"
          fullWidth
          displayEmpty
          disabled={carregando}
          endAdornment={carregando ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
          renderValue={
            field.value !== ''
              ? undefined
              : () => <span className="drawer-placeholder">{placeholder}</span>
          }
        >
          <MenuItem value="">
            <em>Nenhum</em>
          </MenuItem>
          {opcoes.map(opcao => (
            <MenuItem key={opcao.codigo} value={opcao.codigo}>
              {opcao.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </Field>
  </Box>
);

const DrawerFiltros = ({ aberto, onFechar, onAplicarFiltros, onLimparFiltros, filtrosAplicados }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    formikRef,
    valoresIniciais,
    handleSubmit,
    handleLimpar,
    empresas,
    carteiras,
    ocorrenciasBancarias,
    tiposDocumento,
    bancos,
    carregandoFiltros
  } = useDrawerFiltrosHook({ onAplicarFiltros, onLimparFiltros, filtrosAplicados });

  return (
    <Drawer
      anchor="right"
      open={aberto}
      onClose={onFechar}
      PaperProps={{
        className: 'drawer-filtros-paper',
        sx: { width: isMobile ? '100%' : 480 }
      }}
    >
      <Formik
        innerRef={formikRef}
        initialValues={valoresIniciais}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleSubmit: submitForm, setFieldValue }) => (
          <Box className="drawer-filtros-wrapper">
            {/* Header */}
            <Box className="drawer-filtros-header">
              <Box className="drawer-filtros-header-titulo">
                <FilterListIcon />
                <Typography variant="h6" className="drawer-filtros-header-texto">
                  Filtros
                </Typography>
              </Box>
              <IconButton onClick={onFechar} className="drawer-filtros-header-fechar">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            <Box className="drawer-filtros-body">
              {/* 1. Identificação */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Identificação
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Nº Remessa Banco — De"
                    name="remessaBancoDe"
                    type="number"
                    mascara="numero"
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Nº Remessa Banco — Até"
                    name="remessaBancoAte"
                    type="number"
                    mascara="numero"
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Operação" name="operacao" type="number" mascara="numero" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Documento" name="documento" />
                </Grid>
                <Grid item xs={12}>
                  <CampoSelect
                    label="Banco"
                    name="banco"
                    opcoes={bancos}
                    carregando={carregandoFiltros}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Código da Remessa — De"
                    name="codigoRemessaDe"
                    type="number"
                    mascara="numero"
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Código da Remessa — Até"
                    name="codigoRemessaAte"
                    type="number"
                    mascara="numero"
                  />
                </Grid>
                <Grid item xs={12}>
                  <CampoTexto label="Cód. Título" name="codigoTitulo" mascara="numero" />
                </Grid>
              </Grid>

              <Divider className="drawer-secao-divider" />

              {/* 2. Pessoas */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Pessoas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CampoTexto label="Nome Sacado" name="nomeSacado" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Nome Cedente" name="nomeCedente" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="CNPJ Sacado"
                    name="cnpjSacado"
                    mascara="cnpj"
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="CNPJ Cedente"
                    name="cnpjCedente"
                    mascara="cnpj"
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                  />
                </Grid>
              </Grid>

              <Divider className="drawer-secao-divider" />

              {/* 3. Datas */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Datas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CampoTexto label="Vencimento — De" name="vencimentoDe" type="date" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Vencimento — Até" name="vencimentoAte" type="date" />
                </Grid>

                <Grid item xs={6}>
                  <CampoTexto label="Entrada — De" name="entradaDe" type="date" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Entrada — Até" name="entradaAte" type="date" />
                </Grid>

                <Grid item xs={6}>
                  <CampoTexto label="Incluído — De" name="incluidoDe" type="date" />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto label="Incluído — Até" name="incluidoAte" type="date" />
                </Grid>
              </Grid>

              <Divider className="drawer-secao-divider" />

              {/* 4. Financeiro */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Financeiro
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Valor — De"
                    name="valorDe"
                    type="number"
                    placeholder="R$ 0,00"
                    mascara="valor"
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoTexto
                    label="Valor — Até"
                    name="valorAte"
                    type="number"
                    placeholder="R$ 0,00"
                    mascara="valor"
                  />
                </Grid>
              </Grid>

              <Divider className="drawer-secao-divider" />

              {/* 5. Documento */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Documento
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CampoTexto label="Nº Nota Fiscal" name="notaFiscal" mascara="numero" />
                </Grid>
                <Grid item xs={6}>
                  <CampoSelect
                    label="Tipo de Documento"
                    name="tipoDocumento"
                    opcoes={tiposDocumento}
                    carregando={carregandoFiltros}
                  />
                </Grid>
              </Grid>

              <Divider className="drawer-secao-divider" />

              {/* 6. Configurações */}
              <Typography variant="subtitle2" className="drawer-secao-titulo">
                Configurações
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CampoSelect
                    label="Empresa"
                    name="empresa"
                    opcoes={empresas}
                    carregando={carregandoFiltros}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoSelect
                    label="Carteira"
                    name="carteira"
                    opcoes={carteiras}
                    carregando={carregandoFiltros}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CampoSelect
                    label="Ocorrência Bancária"
                    name="ocorrenciaBancaria"
                    opcoes={ocorrenciasBancarias}
                    carregando={carregandoFiltros}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field name="remessasBaixaCustodia">
                    {({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value || false}
                            onChange={e => setFieldValue('remessasBaixaCustodia', e.target.checked)}
                            size="small"
                          />
                        }
                        label="Remessas de Baixa/Custódia"
                        className="drawer-checkbox-label"
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
            </Box>

            {/* Footer */}
            <Box className="drawer-filtros-footer">
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleLimpar}
                className="drawer-btn-limpar"
              >
                Limpar Filtros
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={submitForm}
                className="drawer-btn-filtrar"
              >
                Filtrar
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    </Drawer>
  );
};

export default DrawerFiltros;
